import { useEffect, useMemo, useState } from 'react';

import { Button, Typography } from '@capsule/consumer-design-system';

import { useHistory } from 'react-router';
import { css } from 'styled-components';

import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import { TextUs } from 'src/app/components/Elements/Location';
import Spinner from 'src/app/components/Elements/Spinner';
import { SimpleFeatureLayout } from 'src/app/components/FeatureLayouts/SimpleFeatureLayout/SimpleFeatureLayout';
import { DoctorRequiredMedications } from 'src/app/components/PageSections/DoctorTransferForm/MedicationsFieldArray/DoctorRequiredMedications';
import { MedicationList } from 'src/app/components/PageSections/DoctorTransferForm/MedicationsFieldArray/MedicationList';
import { MedicationListContainer } from 'src/app/components/PageSections/DoctorTransferForm/MedicationsFieldArray/styles';
import type { HealthProfileMedicationTransferrable } from 'src/app/hooks/data/healthProfile';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { medications } from 'src/app/router/paths';
import { CAPSULE_ICON_STRINGS } from 'src/consts';

import type { HealthProfileFormProps } from '..';
import { HealthProfileUpdateStepNames } from '../constants';
import { formatMedicationWithStrengthAndLabel, getMedicationsTransferEligibility } from '../utils';

export const Confirmation = (props: HealthProfileFormProps) => {
  const { onToggleHideHeader, healthProfilePersistentData, isLoadingPersistentData } = props;

  const currentStepName = HealthProfileUpdateStepNames.Confirmation;
  const history = useHistory();
  const goHome = () => history.push(medications.route);
  useEffect(() => onToggleHideHeader(false), []);

  const { doctorRequiredMeds } = useMemo(
    () => getMedicationsTransferEligibility(healthProfilePersistentData.selectedMeds),
    [healthProfilePersistentData.selectedMeds]
  );

  /*
   * we need to keep local state of the meds to display, and not render it from
   * persisted storage, since we have to reset persisted storage in this step
   *
   * This whole block below is dedicated to dealing with our local persisted storage
   * being asynchronous. We have to wait for the data to load to set the local state,
   * then reset persisted data, then wait for the re-set persisted data to percolate
   * and only then set the last visit parameter.
   */
  const [medsTransfered, setMedsTransferred] = useState<HealthProfileMedicationTransferrable[]>([]);
  const [doctorMeds, setDoctorMeds] = useState<HealthProfileMedicationTransferrable[]>([]);

  useEffect(() => {
    if (!isLoadingPersistentData) {
      setMedsTransferred(healthProfilePersistentData.medsToTransfer);
      setDoctorMeds(doctorRequiredMeds);
    }
  }, [doctorRequiredMeds, healthProfilePersistentData, isLoadingPersistentData]);

  return isLoadingPersistentData ? (
    <Spinner centered={true} />
  ) : (
    <SimpleFeatureLayout
      // The nav header and other spacing mess with this page layout on Desktop, and partially hide the CTAs. This is a hack to fix it.
      css={css`
        ${props => props.theme.mediaQueries.small} {
          height: calc(
            100vh + ${({ theme }) => theme.space.loggedInHeaderNavHeight} + ${({ theme }) => theme.space.s8} +
              ${({ theme }) => theme.space.s6}
          );
        }
      `}
      backButton={{ isRendered: false }}
      keyName={currentStepName}
      title={
        <>
          <CapsuleIcon
            iconString={CAPSULE_ICON_STRINGS.check_circle_large_green}
            css={css`
              width: 72px;
              height: 72px;
              svg {
                width: 72px !important;
                height: 72px !important;
              }
            `}
          />
          <Typography
            css={css`
              margin-top: ${({ theme }) => theme.space.s4};
            `}
            variant={{ base: 'displayXl', smallDown: 'heading1' }}
          >
            You're all set
          </Typography>
        </>
      }
      description={
        <div
          css={css`
            margin-top: ${({ theme }) => theme.space.s2};
          `}
        >
          <Typography variant={'body'}>
            We'll get your medications from your pharmacy or doctor and text you a link. This can take 1–2 days.
          </Typography>
          <br />
          <Typography variant={'body'}>
            Have questions? <TextUs>Chat with us</TextUs>
          </Typography>
        </div>
      }
      body={{
        content: (
          <div>
            <TrackViewHealthProfileTransfer />
            <MedicationListContainer>
              <MedicationList
                medications={medsTransfered.map(med => formatMedicationWithStrengthAndLabel(med))}
                hideMedicationHeader
              />
            </MedicationListContainer>
            {doctorMeds.length > 0 && (
              <DoctorRequiredMedications
                medications={doctorMeds.map(med => formatMedicationWithStrengthAndLabel(med))}
              />
            )}
            <Button
              variant="contained-secondary-lg"
              isFullWidth
              onClick={goHome}
              css={css`
                margin-top: ${({ theme }) => theme.space.s6};
              `}
            >
              Done
            </Button>
          </div>
        ),
      }}
    />
  );
};

const TrackViewHealthProfileTransfer = () => {
  useSegmentEvent('viewHealthProfileFormConfirmation', {
    shouldTrackOnMount: true,
    payload: { type: `with_meds_you're_all_set` },
  });

  return null;
};
